<template>
        <a href="#"
           :style="data.styleRect"
           :id="`playspan-${props.pagenum}-${props.annot.id}`"
           @click.prevent="play"
        >
        </a>
</template>

<script>
    import {ref, reactive, onMounted, onUpdated, watchEffect, watch, computed, nextTick}  from "vue";
    import cssvar from '@/scss/_variables.scss';
    import {usePaging} from "@/modules/reader/book/paging";
    import {useBookInfo} from "@/modules/reader/book/bookinfo";
    import {usePageResize} from "@/modules/reader/book/pageResize";
    import {useAnnotationHelper} from "@/modules/reader/annotations/annotationHelper";
    import {updateFileNameIfMultipleSpeech} from  "@/modules/utils/pathUtils";
    import {usePlayBlockControl} from "@/modules/reader/karaoke/playBlockControl";
    import {useKaraokeControl} from "@/modules/reader/karaoke/karaokeControl";
    import {useMuteRoleControl} from "@/modules/reader/karaoke/muteRoleControl";
    import {replaceAudioPathForStagingServer} from  "@/modules/utils/pathUtils";

    const colorRect = cssvar.colorRect;
    const {pageResizeState} = usePageResize();
    const {getResizedAnnotationPos} = useAnnotationHelper();

    export default {
        props: {
            annot:Object,
            pagenum:Number
        },
        setup(props, context) {


            const { setCurrentPage} = usePaging();
            const { playBlockState, playSpan} = usePlayBlockControl();
            const { isInActiveMuteRoles, muteCurrentAudio, unmuteCurrentAudio} = useMuteRoleControl();
            const { bookInfoState, removeActiveAudioSrc } = useBookInfo();

            const data = reactive({
                styleRect:computed(()=>{
                    let domPos = props.annot.domPos;
                    return {
                        ...getResizedAnnotationPos(domPos),
                        background: data.isActive  ? cssvar.colorActiveSpan : "transparent",
                        transform: (domPos.height <= 50) ? "translate(0, 2px) scale(1, 0.9)" : "scale(0.98)",  //adjust for annotation displacement problem....
                        transformOrigin:(domPos.height <= 50) ? "top left" : "center center"  //adjust for annotation displacement problem....
                    }
                }),
                formattedAudioSrc:computed(() => {
                    let path = updateFileNameIfMultipleSpeech(`${bookInfoState.publicPath}/${props.annot.audioSrc}`);
                    //if served on staging (.23), update url to make 206 response available...
                    return replaceAudioPathForStagingServer(path);
                }),
                isActive: computed(()=>{
                    return (
                        (playBlockState.currentSpanIndex >= 0 &&  playBlockState.audioSrcList && playBlockState.audioSrcList.length && data.formattedAudioSrc === playBlockState.audioSrcList[playBlockState.currentSpanIndex]) ||
                        playBlockState.spanSrc === data.formattedAudioSrc
                    )
                })
            });


            const play =()=>{


                    removeActiveAudioSrc();
                    useKaraokeControl().reset();
                    usePlayBlockControl().reset();



                    //The first target should be self (i.e. corresponding audio src of the current button), set the first btnIdPageMapping as target and play it after the current audio finishes.
                    //btnIdPageMapping stores all the target span buttons and the page num, after the first target finishes, the next audios will play one by one until completed all.
                    if (props.annot.isPlayMultipleSpan) {
                        playBlockState.multiSpanStatus.btnIdPageMapping = props.annot.btnIdPageMapping;
                        playBlockState.multiSpanStatus.playedCount = -1;
                        playBlockState.multiSpanStatus.firstSpanPageNum = props.pagenum;
                        nextTick(() => {
                            playSpan(data.formattedAudioSrc, props.pagenum);
                        });

                        //otherwise, it should be simple single span, and reset play block data in this case
                    } else {
                        playSpan(data.formattedAudioSrc, props.pagenum);
                    }


            }

            watchEffect(()=>{

                //Check if the muted role list includes the current span role. If yes, then mute it:
                if (data.isActive) {
                    if (isInActiveMuteRoles(props.pagenum, props.annot.roleId)) {
                        muteCurrentAudio();
                    } else {
                        unmuteCurrentAudio();
                    }
                }

                //This should be watched and executed if the target span in multiple-span mode (i.e. playSpan having a span target) has finished playing.
                if (playBlockState.multiSpanStatus.btnIdPageMapping &&
                    playBlockState.multiSpanStatus.aimPageBtnId === props.annot.id &&
                    playBlockState.multiSpanStatus.aimPageIndex === props.pagenum &&
                    playBlockState.multiSpanStatus.playedCount < playBlockState.multiSpanStatus.btnIdPageMapping.length //&&
                    //playBlockState.multiSpanStatus.playedCount !== -1
                ) {
                    playSpan(data.formattedAudioSrc, props.pagenum);
                }
                //Will be watched and executed if the current span is targeted by a play block button
                else if (playBlockState.multiBlockSpanStatus.isTargeted &&
                    playBlockState.multiBlockSpanStatus.aimPageIndex === props.pagenum &&
                    playBlockState.multiBlockSpanStatus.aimPageBtnId === props.annot.id
                ) {
                    playSpan(data.formattedAudioSrc, props.pagenum);
                }

            })

            return {
                data,
                props,
                playBlockState,
                bookInfoState,
                setCurrentPage,
                playSpan,
                play,
                muteCurrentAudio,
                unmuteCurrentAudio,
                getResizedAnnotationPos
            }

        }
    }

 </script>


<style lang="scss" scoped>
    a {
        z-index:$zindex-playspan !important;
        position:absolute;


    }

</style>